// src/stores/catalogCompatibilityStore.js
//
// The stored catalog compatibility answers (catalogCompatibility/current), live.
// docs/features/catalog_compatibility_single_source_FEATURE.md §3.3
//
// The rules live in ONE place: functions-shared/catalog/compatibilityRules.js. The CRM resolves the whole
// catalog there whenever it changes and stores the answers; this store only READS them. Nothing here
// decides compatibility — an unlisted pair is compatible and an unlisted product has no requirement,
// because that is what the stored answers mean. If you are about to add a rule here, add it to the
// rules module instead, or the Designer, kitting and the engine will disagree again.
//
// Access: collections.catalogCompatibility mirrors costCatalog's readers (seeded by
// scripts/seed-catalog-compatibility-access-control.js).

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import { db } from '@/services/firebaseconfig'
import { doc } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'

const COMPATIBLE = Object.freeze({ status: 'compatible', note: '', authoredOnId: null })
const pairKey = (aId, bId) => (aId < bId ? JSON.stringify([aId, bId]) : JSON.stringify([bId, aId]))
// Display order for the Designer's single chip ONLY — which finding to show first. It never changes an
// answer: every status below was decided by the rules module.
const DISPLAY_RANK = { unavailable: 3, incompatible: 2, unmet: 2, discouraged: 1, compatible: 0 }

export const useCatalogCompatibilityStore = defineStore('catalogCompatibility', () => {
  // The stored doc as plain data, replaced wholesale on every snapshot (shallowRef: no deep proxies, and
  // the SDK Timestamp in computedAt is converted before it ever reaches reactive state — §6.4c).
  const answers = shallowRef(null)
  // 'idle' | 'loading' | 'ready' | 'missing' | 'denied' | 'error'
  const status = ref('idle')
  let _unsubscribe = null

  function subscribe() {
    if (_unsubscribe) return
    status.value = 'loading'
    _unsubscribe = onSnapshot(
      doc(db, 'catalogCompatibility', 'current'),
      snap => {
        if (!snap.exists()) {
          answers.value = null
          status.value = 'missing'
          return
        }
        const data = snap.data()
        answers.value = Object.freeze({
          ...data,
          computedAt: data.computedAt?.toMillis ? data.computedAt.toMillis() : null,
        })
        status.value = 'ready'
      },
      error => {
        answers.value = null
        if (error?.code === 'permission-denied') {
          status.value = 'denied'
          console.debug('[catalogCompatibilityStore] snapshot permission-denied')
        } else {
          status.value = 'error'
          console.error('[catalogCompatibilityStore] onSnapshot error:', error)
        }
      }
    )
  }

  function unsubscribeAll() {
    if (_unsubscribe) {
      _unsubscribe()
      _unsubscribe = null
    }
    answers.value = null
    status.value = 'idle'
  }

  const index = computed(() => {
    const data = answers.value || {}
    return {
      pairs: new Map((data.pairs || []).map(p => [pairKey(p.a, p.b), p])),
      requirements: new Map(
        (data.requirements || []).map(r => [r.productId, r.requiresAnyOf || []])
      ),
      inbound: new Map((data.inbound || []).map(r => [r.productId, r.entries || []])),
      errors: new Map((data.errors || []).map(r => [r.productId, r.errors || []])),
    }
  })

  const isReady = computed(() => status.value === 'ready')
  const isUnavailable = computed(() => ['missing', 'denied', 'error'].includes(status.value))

  /** The stored answer for two products, or null while the answers are not loaded. */
  function pair(aId, bId) {
    if (!isReady.value) return null
    if (!aId || !bId || aId === bId) return COMPATIBLE
    const found = index.value.pairs.get(pairKey(aId, bId))
    return found
      ? { status: found.status, note: found.note || '', authoredOnId: found.authoredOnId }
      : COMPATIBLE
  }
  const requirementsOf = productId => index.value.requirements.get(productId) || []
  const inboundOf = productId => index.value.inbound.get(productId) || []
  const errorsOf = productId => index.value.errors.get(productId) || []

  /**
   * The Designer's one chip for a set of selected products: every stored pair answer between them, plus
   * every stored requirement none of them satisfies (refused like an incompatible pair — Erik, 2026-09-14).
   * Returns the finding to show first, or null when there is nothing to say yet.
   * @param {string[]} productIds  selected catalog ids
   * @param {(id: string) => string} nameOf
   */
  function designFinding(productIds, nameOf = id => id) {
    const ids = [...new Set((productIds || []).filter(Boolean))]
    if (!ids.length || status.value === 'idle' || status.value === 'loading') return null
    if (isUnavailable.value) {
      return { status: 'unavailable', note: 'the compatibility check could not be loaded' }
    }
    const findings = []
    for (let i = 0; i < ids.length; i += 1) {
      for (let j = i + 1; j < ids.length; j += 1) findings.push(pair(ids[i], ids[j]))
    }
    const selected = new Set(ids)
    for (const id of ids) {
      const required = requirementsOf(id)
      if (required.length && !required.some(requiredId => selected.has(requiredId))) {
        findings.push({
          status: 'unmet',
          note: `${nameOf(id)} requires ${required.map(nameOf).join(' or ')}`,
        })
      }
    }
    if (!findings.length) return null
    return findings.reduce((shown, next) =>
      (DISPLAY_RANK[next.status] ?? 0) > (DISPLAY_RANK[shown.status] ?? 0) ? next : shown
    )
  }

  return {
    answers,
    status,
    isReady,
    isUnavailable,
    subscribe,
    unsubscribeAll,
    pair,
    requirementsOf,
    inboundOf,
    errorsOf,
    designFinding,
  }
})
